import React__default, { forwardRef } from 'react';
import * as Popover$1 from '@radix-ui/react-popover';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Popover Root
// ------------------------------------------------------------------
/** Contains all the parts of a Popover */
const Root = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Popover$1.Root, props, children);
};
Root.displayName = "Root";
// Popover Trigger
// ------------------------------------------------------------------
/** The button that toggles the popover. By default, the Popover.Content will position itself against the trigger. */
const Trigger = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(Popover$1.Trigger, _extends({
    ref: innerRef
  }, props), children);
});

// Popover Anchor
// ------------------------------------------------------------------
/** An optional element to position the Popover.Content against. If this part is not used, the content will position alongside the Popover.Trigger. */
const Anchor = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(Popover$1.Anchor, _extends({
    ref: innerRef
  }, props), children);
});

// Popover Portal
// ------------------------------------------------------------------
/** When used, portals the content part into the body. */
const Portal = ({
  children,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Popover$1.Portal, props, children);
};
Portal.displayName = "Portal";
// Popover Content
// ------------------------------------------------------------------
/** The component that pops out when the popover is open. */
const Content = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(Popover$1.Content, props, children);
});

// Popover Arrow
// ------------------------------------------------------------------
/** An optional arrow element to render alongside the popover. This can be used to help visually link the anchor with the Popover.Content. Must be rendered inside Popover.Content. */
const Arrow = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(Popover$1.Arrow, _extends({
    ref: innerRef
  }, props), children);
});

// Popover Close
// ------------------------------------------------------------------
/** The button that closes an open popover. */
const Close = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, forwardedRef) => {
  const innerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  return /*#__PURE__*/React__default.createElement(Popover$1.Close, _extends({
    ref: innerRef
  }, props), children);
});

// Exports
Root.displayName = 'Popover';
Trigger.displayName = 'Trigger';
Anchor.displayName = 'Anchor';
Portal.displayName = 'Portal';
Content.displayName = 'Content';
Arrow.displayName = 'Arrow';
Close.displayName = 'Close';

/** A popover displays rich content in a portal, triggered by a button. */
var Popover = Object.assign(Root, {
  Trigger,
  Anchor,
  Portal,
  Content,
  Arrow,
  Close
});

export { Popover as default };
